import { FC, Fragment, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';

import { Button, ErrorMessage, Loading } from '@components/base';

type LayoutQueryResult = UseQueryResult & {
  context?: string;
};

export type QueryTemplateProps = PropsWithChildren<{
  dense?: boolean;
  queryResult: LayoutQueryResult | LayoutQueryResult[];
}>;

export const QueryTemplate: FC<QueryTemplateProps> = ({ children, dense, queryResult }) => {
  const { t } = useTranslation();

  const queryResultList = Array.isArray(queryResult) ? queryResult : [queryResult];
  const anyLoading = queryResultList.some(({ isLoading }) => isLoading);
  const hasError = queryResultList.some(({ error }) => !!error);
  const hasData = queryResultList.every(({ data }) => !!data);

  const refetchAll = () => queryResultList.map(({ refetch }) => refetch());

  return hasError ? (
    <Stack>
      {queryResultList.map(({ context, error }) =>
        error ? (
          <Box key={context} p={dense ? undefined : 4}>
            <ErrorMessage
              action={
                <Button
                  color='inherit'
                  leftIcon='replay'
                  loading={anyLoading}
                  onClick={refetchAll}
                  variant='text'
                >
                  {t('retry')}
                </Button>
              }
              error={error}
            />
          </Box>
        ) : null
      )}
    </Stack>
  ) : hasData ? (
    <Fragment>{children}</Fragment>
  ) : anyLoading ? (
    <Loading BoxProps={{ p: 4 }} />
  ) : null;
};
